import React, { memo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { PartnerDriverDetailsContent } from 'src/components/partner-driver/details/PartnerDriverDetailsContent'
import { PartnerDriverLoadState } from 'src/components/partner-driver/details/PartnerDriverLoadState'
import { usePartnerDriverLoader } from 'src/hooks/usePartnerDriverLoader'
import {
  getPartnerDriverSummary,
  getPartnerDriverSummaryError,
  getPartnerDriverSummaryLoading,
} from 'src/selectors/partnerDriver'

/**
 * One driver as its own page. Two reads: `/steps` (driver, partner, steps) and
 * `/search?partnerDriverId=` (OTP code + pending Yardstik reports, which `/steps` lacks).
 * The page refetches after every write — the slice never patches the driver locally. The steps
 * table itself is on `PartnerDriverStepsPage`.
 */
const PartnerDriverPageComponent = () => {
  const {
    driver,
    isIdValid,
    isDriverLoading,
    driverError,
    isNotFound,
    searchBackTo,
    navigationState,
    refreshDriver,
    refreshSummary,
    refreshAll,
  } = usePartnerDriverLoader({ withSummary: true })

  const summary = useSelector(getPartnerDriverSummary)
  const isSummaryLoading = useSelector(getPartnerDriverSummaryLoading)
  const summaryError = useSelector(getPartnerDriverSummaryError)

  // The summary belongs to whichever driver was last open; only this driver's row is shown.
  const driverSummary = summary && driver && summary.id === driver.id ? summary : null

  return (
    <div
      data-testid="partner-driver-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-y-auto p-6`}
    >
      {driver ? (
        <PartnerDriverDetailsContent
          driver={driver}
          summary={driverSummary}
          summaryLoading={isSummaryLoading}
          summaryError={summaryError}
          backTo={searchBackTo}
          navigationState={navigationState}
          onDriverChanged={refreshDriver}
          onProfileSaved={refreshAll}
          onRetrySummary={refreshSummary}
        />
      ) : (
        <PartnerDriverLoadState
          isNotFound={!isIdValid || isNotFound}
          driverError={driverError}
          isDriverLoading={isDriverLoading}
          searchBackTo={searchBackTo}
          onRetry={refreshAll}
        />
      )}
    </div>
  )
}

export const PartnerDriverPage = memo(PartnerDriverPageComponent)
